import { KText } from '@/ui-library/components';
import {
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  PopoverArrow,
  Divider,
  Portal,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import { Mail02 } from '@kula-ai/icons/dual-tone/Mail02';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { Eye } from '@kula-ai/icons/dual-tone/Eye';
import { useMutation } from '@tanstack/react-query';
import { getPlaceholderPreview } from '@/components/formBuilder/formFields/FormEditor/api';
import React, { useState, useMemo, useCallback } from 'react';
import { HtmlRenderer } from '@/components/ats/HtmlRenderer';
import { CenterSpinner } from '@/components/ats';
import { useAuth } from '@/hooks/use-auth';

export const SchBulkCalendarInvite = ({
  interviewPlan,
  applicationData,
  updateSharedData,
  registerForUpdates,
  interviewDataRef,
  currentTemplateData,
  updateTemplateModificationData,
}) => {
  const [previewData, setPreviewData] = useState({
    candidate: undefined,
    reviewer: undefined,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    candidate_comm_template: candidateTemplate,
    reviewer_comm_template: reviewerTemplate,
  } = interviewPlan ?? {};

  const candidatePreviewMutation = useMutation({
    mutationFn: getPlaceholderPreview,
    onSuccess: (data) => {
      setPreviewData((prev) => ({ ...prev, candidate: data }));
    },
    onError: (error) => {
      console.error('Candidate preview failed:', error);
    },
  });

  const reviewerPreviewMutation = useMutation({
    mutationFn: getPlaceholderPreview,
    onSuccess: (data) => {
      setPreviewData((prev) => ({ ...prev, reviewer: data }));
    },
    onError: (error) => {
      console.error('Reviewer preview failed:', error);
    },
  });

  const { currUser } = useAuth();

  const commonPreviewPayload = useMemo(() => {
    const payload = {
      retain_variables: true,
      sender_id: currUser?.id,
      ats_job_id: applicationData?.job_id,
      contact_id: applicationData?.contact?.id,
      ats_job_stage_id: applicationData?.job_stage_id,
    };

    return payload;
  }, [currUser?.id, applicationData]);

  const handlePreviewOpen = useCallback(() => {
    const currentCandidateBody = candidateTemplate?.body;
    const currentReviewerBody = reviewerTemplate?.body;

    // Candidate Preview
    if (
      candidateTemplate &&
      typeof currentCandidateBody === 'string' &&
      !previewData.candidate
    ) {
      const candidatePayload = {
        body: currentCandidateBody,
        ...commonPreviewPayload,
      };

      candidatePreviewMutation.mutate(candidatePayload);
    }

    // Reviewer Preview
    if (
      reviewerTemplate &&
      typeof currentReviewerBody === 'string' &&
      !previewData.reviewer
    ) {
      const reviewerPayload = {
        body: currentReviewerBody,
        ...commonPreviewPayload,
      };

      reviewerPreviewMutation.mutate(reviewerPayload);
    }
  }, [
    candidateTemplate,
    reviewerTemplate,
    previewData,
    candidatePreviewMutation,
    reviewerPreviewMutation,
    commonPreviewPayload,
  ]);

  const previewPopupWrapper = () => {
    return (
      <Popover isOpen={isOpen} onClose={onClose} placement="top-start">
        {/* @ts-ignore */}
        <PopoverTrigger>
          <IconButton
            aria-label="Preview"
            _hover={{
              bg: 'secondary-100',
            }}
            w="32px"
            h="32px"
            minW="32px"
            icon={<Icon color="secondary-700" as={Eye} />}
            variant="ghost"
            onClick={() => {
              onOpen();
              handlePreviewOpen();
            }}
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="640px" h="345px" overflow="hidden">
            <PopoverArrow />
            <PopoverBody
              p="12px 17px"
              border="1px solid"
              borderRadius="6px"
              overflow="hidden"
              borderColor="secondary-100"
              h="100%"
            >
              {candidatePreviewMutation?.isPending ||
              reviewerPreviewMutation?.isPending ? (
                <CenterSpinner h="321px" />
              ) : (
                <Flex gap="12px" height="321px" w="100%">
                  <Flex flexDirection="column" gap="12px" w="48%">
                    <KText color="secondary-900">
                      Candidate's calendar invite
                    </KText>
                    <KText color="secondary-700" overflowY="auto">
                      {previewData.candidate && (
                        <HtmlRenderer
                          htmlString={previewData.candidate?.body}
                        />
                      )}
                    </KText>
                  </Flex>
                  <Divider
                    h="345px"
                    mt="-12px"
                    orientation="vertical"
                    borderColor="secondary-100"
                  />
                  <Flex flexDirection="column" gap="12px" w="48%">
                    <KText color="secondary-900">
                      Interviewer's calendar invite
                    </KText>
                    <KText color="secondary-700" overflowY="auto">
                      {previewData.reviewer && (
                        <HtmlRenderer htmlString={previewData.reviewer?.body} />
                      )}
                    </KText>
                  </Flex>
                </Flex>
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  };

  const renderHeader = () => {
    const iconButtonStyles = {
      w: '32px',
      h: '32px',
      bg: 'base-white',
      border: '1px solid',
      borderRadius: '6px',
      alignItems: 'center',
      justifyContent: 'center',
      borderColor: 'secondary-100',
    };

    return (
      <Flex justifyContent="space-between">
        <Flex alignItems="center" gap="6px">
          <Flex>
            <Icon color="secondary-700" as={Mail02} />
          </Flex>
          <KText color="secondary-900">Calendar invite</KText>
        </Flex>
        <Flex gap="3px">
          <Flex {...iconButtonStyles}>{previewPopupWrapper()}</Flex>
          <Flex
            {...iconButtonStyles}
            _hover={{
              bg: 'secondary-100',
            }}
          >
            <Icon
              cursor="pointer"
              color="secondary-700"
              as={Edit05}
              onClick={() => {
                // Store original templates in bulk-level state if not already stored
                const bulkUpdateData = {};
                if (
                  !currentTemplateData?.candidateOriginalTemplate &&
                  candidateTemplate
                ) {
                  bulkUpdateData['candidateOriginalTemplate'] = {
                    id: candidateTemplate.id,
                    body: candidateTemplate.body,
                    name: candidateTemplate.name,
                  };
                }

                if (
                  !currentTemplateData?.reviewerOriginalTemplate &&
                  reviewerTemplate
                ) {
                  bulkUpdateData['reviewerOriginalTemplate'] = {
                    id: reviewerTemplate.id,
                    body: reviewerTemplate.body,
                    name: reviewerTemplate.name,
                  };
                }

                // Update bulk-level template data
                if (
                  Object.keys(bulkUpdateData).length > 0 &&
                  updateTemplateModificationData
                ) {
                  updateTemplateModificationData(bulkUpdateData);
                }

                updateSharedData({ editEmailInvites: true });
              }}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  };

  // Helper function to get template name with modification check
  const getTemplateName = (template, templateType) => {
    if (!template) return 'No template selected';

    // Read modification flag from bulk-level state (persistent across switches)
    const modificationKey = `${templateType}TemplateModified`;
    const isModified = currentTemplateData?.[modificationKey] === true;

    // If template has been modified, always show as custom
    if (isModified) {
      return 'Custom template';
    }

    // If template has a name, use it
    if (template.name && template.name.trim()) {
      return template.name;
    }

    // For templates without names, determine if it's default or custom
    if (template.kind === 'one_time_template') {
      return 'Custom template';
    }

    if (template.kind && template.kind !== 'one_time_template') {
      return 'Default template';
    }

    return 'Custom invite description';
  };

  const renderBody = () => {
    return (
      <Flex flexDirection="column" gap="6px">
        <Flex flexDirection="column" gap="3px">
          <KText color="secondary-900">Candidate</KText>
          <KText color="secondary-700">
            {getTemplateName(candidateTemplate, 'candidate')}
          </KText>
        </Flex>
        <Flex flexDirection="column" gap="3px">
          <KText color="secondary-900">Interviewer</KText>
          <KText color="secondary-700">
            {getTemplateName(reviewerTemplate, 'reviewer')}
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex
      mt="8px"
      gap="6px"
      p="12px 17px"
      bg="primary-25"
      borderRadius="6px"
      flexDirection="column"
    >
      {renderHeader()}
      {renderBody()}
    </Flex>
  );
};
